import { defineStore } from "pinia";
import Crud from "@/store/NewsFeed/crud";
import axios from "axios";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { useLK } from "@/controllerLK/LKstore.js";
import {useNotificationsStore} from "@/store/notifications";
import { t } from "@/controllerLK/AvalLocalization";
import {addMissingElements} from "@/functions/basic";
import { cardsApi } from "@/composables/api/cards.api";
import { blocksToHtml, blocksToText } from "@/composables/api/cards.blocks";

// Приводит CardDetailResponse из ms.cards к форме, которую ожидает существующий
// UI (id/raw_text/text/date/domain/rubric_ids и т.п.). Без этой прослойки пришлось
// бы менять все компоненты News*.
function mapCardToLegacy(c) {
    if (!c) return null;
    // Новый контракт: одна статья (article_id) может попасть в несколько рубрик,
    // и они возвращаются массивом `rubrics: [{ rubric_id, rubric_name, card_id, matched_terms }]`.
    // Старый контракт (card_id / rubric_id / rubric_highlights) держим как fallback.
    const rubrics = Array.isArray(c.rubrics) ? c.rubrics : [];
    const primaryId = c.article_id ?? c.card_id ?? rubrics[0]?.card_id ?? null;
    const rubricIds = rubrics.length
        ? rubrics.map((r) => r.rubric_id).filter(Boolean)
        : (c.rubric_id ? [c.rubric_id] : []);
    const rubricHighlights = rubrics.length
        ? Array.from(new Set(rubrics.flatMap((r) => r.matched_terms || [])))
        : (c.rubric_highlights || []);
    return {
        id: primaryId,
        card_id: primaryId,
        article_id: c.article_id ?? null,
        title: c.title || "",
        raw_text: blocksToHtml(c.content),
        text: blocksToText(c.content),
        content: c.content,
        published_at: c.published_at,
        fetched_at: c.fetched_at,
        date: toUnixSeconds(c.published_at || c.fetched_at),
        is_favourite: !!c.is_favourite,
        sentiment: c.sentiment ?? null,
        importance: c.importance ?? null,
        language: c.language || null,
        url: c.url || "",
        domain: extractDomain(c.url || c.source?.url),
        source: c.source || null,
        source_name: c.source?.name || "",
        rubrics,
        rubric_ids: rubricIds,
        tags: c.tags || [],
        source_tag_ids: (c.tags || []).map((tag) => tag.id),
        // ms.cards теперь возвращает cities: CityRef[] (модель определения городов
        // на CPU). Держим и cities-объекты (для карты/чипов), и плоский city_ids.
        cities: (c.cities || []).map(mapCity).filter(Boolean),
        city_ids: (c.cities || []).map((x) => x?.id).filter(Boolean),
        ner: c.ner || null,
        query_highlights: c.query_highlights || [],
        rubric_highlights: rubricHighlights,
        category_id: null,
        is_translated: false,
    };
}

// Нормализуем город из ответа ms.cards. UI (SettingsItemRegions, карта и др.)
// исторически читает `item.city`, а спека даёт `name` — добавляем алиас, чтобы
// не переписывать все места использования.
function mapCity(c) {
    if (!c || !c.id) return null;
    return {
        id: c.id,
        name: c.name || "",
        city: c.name || "",
        count: typeof c.count === "number" ? c.count : undefined,
        lat: c.lat ?? null,
        lon: c.lon ?? null,
    };
}

function toUnixSeconds(iso) {
    if (!iso) return null;
    const ts = new Date(iso).getTime();
    return Number.isFinite(ts) ? Math.floor(ts / 1000) : null;
}

function extractDomain(url) {
    if (!url) return "";
    try { return new URL(url).hostname; } catch (_) { return ""; }
}

function toIsoFromUnix(unix) {
    if (!unix) return undefined;
    return new Date(Number(unix) * 1000).toISOString();
}

const crud = new Crud(
    process.env.VUE_APP_BASE_API + "Rubrics",
    "id",
    "",
    {}
);

const todayDate = new Date();
const sevenDayBefore = Date.parse(
    new Date(todayDate.setTime(todayDate.getTime() - (6 * 24 * 60 * 60 * 1000))).toISOString().split("T")[0]) / 1000;

const today = () => Date.parse(new Date()) / 1000;

let controller;
export const useNewsFeedStore = defineStore({
  id: "newsFeed",
  state: () => ({
    ...crud.state,
    currentNews: null,
    take: 20,
    currentImportance: [],
    isLastNews: false,
    isMarked: false,
    dateFrom: sevenDayBefore,
    dateTo: today(),
    favoriteVisible: false,
    abort: false,
    lastSearchedValue: null,
    searchValue: "",
    searchValueList: [],
    deleteCardList: [],
    totalCount: 1,
    pointer: false,
    facetParams: null,
    initialFacetObjects: {
      persons: [],
      locations: [],
      organisations: [],
    },
    onFilterTags: [],
    emptyNews: false,
    popupCardRubrics: false,
    popupCardTags: false,
    filterRegion: {
      title: "region",
      inputValue: "",
      list: [],
      filterList: [],
      nameList: [],
      condition: false
    },
    filterImportance: {
      title: "importance",
      list: []
    },
    filterSentiment: {
      title: "sentiment",
      list: []
    },
    filterCategories: {
      title: "categories",
      list: [],
      filterList: []
    },
    filterTags: {
      title: "tags",
      inputValue: "",
      list: [],
      listID: [],
      condition: true
    },
    filterPoliticalAffiliation: {
      title: "political",
      inputValue: "",
      value: "not_chosen",
      condition: true
    },
    filterObject: {
      title: "object",
      inputValue: "",
      list: [],
      listID: {
        persons: [],
        locations: [],
        organisations: []
      },
      condition: true
    },
    filterPersons: {
      title: "persons",
      inputValue: "",
      list: [],
      listID: [],
      condition: true
    },
    filterOrganisations: {
      title: "organisations",
      inputValue: "",
      list: [],
      listID: [],
      condition: true
    },

    downloadOn: false,
    downloadList: [],
    projectCities: [],
    useSemSearch: false,
    // Курсор пагинации ms.cards. Сбрасывается при смене фильтров/рубрик.
    nextCursor: null,
    hasMore: false,
  }),
  getters: {
    ...crud.getters,
    getFiltredItems(state) {
      if (!state.items?.length) return;
      if (state.currentImportance === 0) {
        return state.items;
      }
      return (state.items || []).filter(
          (el) => el.importance === state.currentImportance
      );
    },
  },
  actions: {
    ...crud.actions,
    async translateNewsText(title, text, id) {
      try {
        const result = await axios.post("/translator/api/v1/translate", {
          id: id.toString(),
          title: title,
          text: text,
          to_language: "ru"
        });

        this.currentNews.is_translated = true;
        return {
          title: result.data.title,
          raw_text: result.data.text
        };
      } catch (e) {
        console.log(e);
        this.currentNews.is_translated = false;
        return text;
      }
    },
    // Загрузка карточек через ms.cards (POST /cards/search). getNews оставлен как
    // алиас на getRubricNews — обе точки исторически звали одну и ту же логику.
    async getNews() {
      return this.getRubricNews();
    },

    async getRubricNews() {
      await this.fetchCardsPage({ favouriteOnly: false, append: false });
    },

    // Догрузка следующей страницы курсорной пагинации. Работает только «вперёд».
    async loadMoreNews() {
      if (!this.nextCursor || !this.hasMore || this.loading) return;
      await this.fetchCardsPage({ favouriteOnly: this.favoriteVisible, append: true });
    },

    async fetchCardsPage({ favouriteOnly = false, append = false } = {}) {
      if (controller) controller.abort();
      controller = new AbortController();

      this.loading = true;
      this.emptyNews = false;
      if (!append) {
        this.currentNews = null;
        this.nextCursor = null;
      }

      // Задача 1791 — минимум 20 карточек при переходе из «маленького» проекта.
      if (this.take < 20) this.take = 20;

      try {
        const { data } = await this.getDataForNews({ favouriteOnly });
        this.applyCardsResponse(data, { append });
      } catch (err) {
        if (axios.isCancel?.(err) || err?.name === "CanceledError") return;
        this.setError(err);
      } finally {
        this.loading = false;
      }
    },

    // Общая логика распаковки ответа /cards/search в state. Facet-поля, которых
    // нет в новом API (persons/locations/organisations/categories), оставляем
    // как есть — старые UI-фильтры показывают пустые списки. Города теперь
    // приходят из фасета и оседают в filterRegion.list по union'у (справочника
    // /cities у нас больше нет — накапливаем каталог из фактических выдач).
    applyCardsResponse(data, { append = false } = {}) {
      const nextItems = data.cards || [];
      this.items = append ? [...(this.items || []), ...nextItems] : nextItems;
      this.totalCount = data.total_count || 0;
      this.take = this.totalCount < 20 && this.totalCount > 0 ? this.totalCount : this.take;
      this.facetParams = data;
      this.nextCursor = data.next_cursor || null;
      this.hasMore = !!data.has_more;
      this.onFilterTags = data.tags || [];
      if (!append) this.currentNews = this.items[0] || null;
      this.emptyNews = !this.items.length;

      // Города: копим объединение уже виденных, чтобы выбор пользователя не
      // исчезал из списка, когда фасет сужается при активных фильтрах.
      if (Array.isArray(data.cities) && data.cities.length) {
        const known = new Set((this.filterRegion.list || []).map((c) => c.id));
        for (const city of data.cities) {
          if (city?.id && !known.has(city.id)) {
            this.filterRegion.list.push(city);
            known.add(city.id);
          }
        }
      }

      // Заполняем legacy-фасеты пустыми массивами, чтобы addMissingElements
      // ниже по коду не падал, если какой-то компонент их дёрнет.
      addMissingElements(this.initialFacetObjects.persons, data.persons || []);
      addMissingElements(this.initialFacetObjects.locations, data.locations || []);
      addMissingElements(this.initialFacetObjects.organisations, data.organisations || []);
    },

    async getCategoriesList() {
      await axios.get(process.env.VUE_APP_BASE_API + "Categories/list").then((res) => {
        this.filterCategories.list = res.data ? res.data : [];
      });
    },

    // Временно отключено: Cities/{projectId} — старый API, куки gateway2 ему не подходят.
    async getCities() {
      this.filterRegion.list = [];
      this.projectCities = [];
    },

    clearNewsFeed() {
      this.totalCount = 0;
      this.isLastNews = false;
      this.items = [];
      this.currentNews = null;
      this.nextCursor = null;
      this.hasMore = false;
    },

    clearFacet() {
      this.filterTags.condition = true;
      this.filterObject.condition = true;
      this.filterRegion.condition = true;
      this.filterRegion.list = [];
      this.filterRegion.inputValue = "";
      this.filterRegion.filterList = [];

      this.filterImportance.list = [];

      this.filterSentiment.list = [];

      this.filterCategories.list = [];
      this.filterCategories.filterList = [];

      this.filterTags.list = [];
      this.filterTags.listID = [];
      this.filterTags.inputValue = "";

      this.filterObject.list = [];
      this.filterObject.listID.persons = [];
      this.filterObject.listID.locations = [];
      this.filterObject.listID.organisations = [];
      this.filterObject.inputValue = "";

      this.dateFrom = sevenDayBefore;
      this.dateTo = today();
      this.facetParams = null;
      this.nextCursor = null;
      this.hasMore = false;
      this.initialFacetObjects = {
        persons: [],
        locations: [],
        organisations: [],
      };
    },

    // Сбрасываем курсор и фасеты — следующая загрузка пойдёт с начала.
    async updateFacet() {
      this.facetParams = null;
      this.nextCursor = null;
    },

    // Обёртка над POST /cards/search. Возвращает объект в форме
    // { data: { total_count, cards, tags, next_cursor, has_more, persons, locations, organisations, cities } },
    // чтобы старые вызывающие места читали data.cards, data.total_count и т.п.
    async getDataForNews({ favouriteOnly = false } = {}) {
      controller = new AbortController();
      const rubricsStore = useRubricsStore();
      // rubricsStore.chosenRubrics уже наполняется из выбранной темы (см. themes.js),
      // поэтому отдельно передавать theme_ids в /cards/search не требуется.
      const chosenRubrics = rubricsStore.chosenRubrics || [];

      const body = {
        project_id: useLK().project?.id,
        q: this.searchValue || undefined,
        rubric_ids: chosenRubrics?.length ? chosenRubrics : undefined,
        sentiments: this.filterSentiment.list?.length ? this.filterSentiment.list : undefined,
        importance: this.filterImportance.list?.length ? this.filterImportance.list : undefined,
        favourite: favouriteOnly ? true : undefined,
        date_from: toIsoFromUnix(this.dateFrom),
        date_to: toIsoFromUnix(this.dateTo),
        after: this.nextCursor || undefined,
        page_size: this.take > 0 ? this.take : 20,
      };
      if (this.filterTags.listID?.length) {
        body.tag_ids = {
          values: this.filterTags.listID,
          mode: this.filterTags.condition ? "all" : "any",
        };
      }
      if (this.filterRegion.filterList?.length) {
        body.city_ids = {
          values: this.filterRegion.filterList,
          mode: this.filterRegion.condition ? "all" : "any",
        };
      }

      const res = await cardsApi.search(body, { signal: controller.signal });

      // Раскладываем ответ в форму, которую ждёт applyCardsResponse и старый код.
      return {
        data: {
          total_count: res?.total || 0,
          cards: (res?.items || []).map(mapCardToLegacy),
          tags: res?.facets?.tags || [],
          cities: (res?.facets?.cities || []).map(mapCity).filter(Boolean),
          next_cursor: res?.next_cursor || null,
          has_more: !!res?.has_more,
          persons: [],
          locations: [],
          organisations: [],
        },
      };
    },

    // PATCH /cards/article/{article_id}?project_id=... — обновляет проектные
    // поля статьи (sentiment / importance / is_favourite). В items[].id лежит
    // article_id (см. mapCardToLegacy), project_id берём из активного проекта.
    async updateCard(cardId, updates) {
      if (!cardId || !updates) return false;
      const projectId = useLK().project?.id;
      if (!projectId) return false;
      try {
        await cardsApi.updateArticle(cardId, projectId, updates);
        return true;
      } catch (e) {
        this.setError(e);
        return false;
      }
    },

    // Добавление новости в избранное
    async setFavorite(cardId, isFavorite) {
      const success = await this.updateCard(cardId, { is_favourite: isFavorite });
      if (success) {
        this.items.forEach(item => {
          if (item.id === cardId) item.is_favourite = isFavorite;
        });
        if (this.currentNews?.id === cardId) this.currentNews.is_favourite = isFavorite;
      }
    },

    // Избранное — тот же /cards/search с favourite=true.
    async getFavorite() {
      this.items = [];
      await this.fetchCardsPage({ favouriteOnly: true, append: false });
    },

    // Временно отключено: cards/{id}/cities — старый API.
    // eslint-disable-next-line no-unused-vars
    async setCityCard(_cardId, _cityList) {},

    // Подсветку слов поиска теперь делает бэкенд (query_highlights) + фронтовый
    // highlightHtml на правой панели. Клиентский обвес в <span class="marked-word">
    // больше не нужен.
    async facetSearch() {
      await this.getRubricNews();
    },

    // DELETE /cards/{id}.
    async deleteCard(cardId) {
      try {
        await cardsApi.remove(cardId);
      } catch (e) {
        this.setError(e);
        return;
      }
      const idx = this.items.findIndex((item) => item.id === cardId);
      if (idx >= 0) this.items.splice(idx, 1);
      if (this.currentNews?.id === cardId) this.currentNews = this.items[0] || null;
    },

    // Изменение сантимента новости
    async changeSentiment(cardId, sentimentValue) {
      const ok = await this.updateCard(cardId, { sentiment: sentimentValue });
      if (ok) {
        const item = this.items.find(i => i.id === cardId);
        if (item) item.sentiment = sentimentValue;
        if (this.currentNews?.id === cardId) this.currentNews.sentiment = sentimentValue;
      }
    },

    // Изменение значимости новости
    async changeImportance(cardId, importanceValue) {
      const ok = await this.updateCard(cardId, { importance: importanceValue });
      if (ok) {
        const item = this.items.find(i => i.id === cardId);
        if (item) item.importance = importanceValue;
        if (this.currentNews?.id === cardId) this.currentNews.importance = importanceValue;
      }
    },

    // Временно отключено: cards/{id}/rubrics, cards/{id}/tags — старый API.
    // eslint-disable-next-line no-unused-vars
    async updateCardRubrics(_cardId, rubricsIds) {
      if (this.currentNews) this.currentNews.rubric_ids = rubricsIds;
    },

    // eslint-disable-next-line no-unused-vars
    async updateCardTags(_cardId, tagsIds) {
      if (this.currentNews) this.currentNews.source_tag_ids = tagsIds;
    },

    // Обновление категории карточки
    async updateCardCategory(cardId, categoryId) {
      const success = await this.updateCard(cardId, { category_id: categoryId });
      if (success) {
        const notificationsStore = useNotificationsStore();
        this.currentNews.category_id = categoryId;
        notificationsStore.createNotify(t("toast.changes_saved"), "success");
      }
    },
    async filesDownload(cardIdList) {
      let url = process.env.VUE_APP_BASE_API + "Files/report";
      const files = await axios.post(url, {
        "projectId": useLK().project.id,
        "cardIDs": cardIdList
      }, {responseType: "blob"});

      const fileUrl = window.URL.createObjectURL(files.data);
      const a = document.createElement("a");
      a.href = fileUrl;
      let filename = files.headers.get("content-disposition");
      filename = filename.match(/(?<=")(?:\\.|[^"\\])*(?=")/)[0];
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(fileUrl);
    },

    // Добавление/Удаление объекта в список фасета.
    // true - Добавлен. false - Удален.
    async toggleObjectInFilter(objectId, objectType) {
      // Проверяем, есть ли уже этот объект в фильтре
      if (this.filterObject.list.some(obj => obj === objectId)) {
        // Если есть, удаляем его
        this.filterObject.list = this.filterObject.list.filter(obj => obj !== objectId);
        this.filterObject.listID[objectType] = this.filterObject.listID[objectType]
            .filter(id => id !== objectId);
        return false;
      } else {
        // Если нет, добавляем
        this.filterObject.list.push(objectId);
        this.filterObject.listID[objectType].push(objectId);
        return true;
      }
    }
  },
});
//
